import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { onboardingRuns } from "../../api/endpoints/onboardingRuns";
import type { RenderBundleOut } from "../../api/types/renderBundle";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import { IDENTITY_ONBOARDING_API, buildRunArtifacts } from "./runTypes";

const COMMIT_RE = /^[0-9a-f]{40}$/;

export function CreateRunPanel({
  bundle,
  stale,
  canWrite,
  requestStatus,
  onCreated,
}: {
  bundle: RenderBundleOut;
  stale: boolean;
  canWrite: boolean;
  requestStatus: string | null; // shown for context only — never gates create
  onCreated: (runId: string) => void;
}) {
  const [commit, setCommit] = useState("");
  const descriptor = IDENTITY_ONBOARDING_API;
  const isApi = descriptor.executionKind === "api";
  const tenantSlug = bundle.tenant_slug;

  const create = useMutation({
    mutationFn: () =>
      onboardingRuns
        .create({
          run_type: descriptor.run_type,
          run_type_version: descriptor.run_type_version,
          artifact_schema_version: descriptor.artifact_schema_version,
          tenant_slug: tenantSlug,
          site_slug: null,
          artifacts: buildRunArtifacts(descriptor, bundle),
          generator_version: bundle.generator_version,
          // api runs send NO commit/executor_ref (executor_ref is server-stamped);
          // ansible runs pin the 40-hex sha.
          ...(isApi ? {} : { playbook_commit: commit.trim() }),
        })
        .then((r) => r.data),
    onSuccess: (run) => onCreated(run.id),
  });

  const commitValid = COMMIT_RE.test(commit.trim());
  const canSubmit = canWrite && !stale && tenantSlug != null && (isApi || commitValid);

  if (!canWrite) return null;

  return (
    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, display: "grid", gap: 10 }}>
      <h4 style={{ margin: 0, fontSize: "0.9rem" }}>Create onboarding run</h4>
      <div style={{ fontSize: "0.8rem", color: "var(--text-dim)" }}>
        Pins this exact render. Tenant <code>{tenantSlug ?? "—"}</code>
        {requestStatus && <> · request status <code>{requestStatus}</code></>}
      </div>
      {stale && (
        <p style={{ color: "var(--warn, #b58900)", fontSize: "0.8rem", margin: 0 }}>
          The site or its request changed since this render — re-render before creating.
        </p>
      )}
      <FormShell
        submitLabel="Create run"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          create.mutate();
        }}
        pending={create.isPending}
        submitDisabled={!canSubmit}
        error={create.error}
        errorTitle="Create run failed"
      >
        {isApi ? (
          <p style={{ fontSize: "0.8rem", color: "var(--text-dim)", margin: 0 }}>
            Runs in-process in the Hub (no playbook commit) — the executor ref is stamped
            from the deployed Hub build.
          </p>
        ) : (
          <TextField
            label="Playbook commit"
            value={commit}
            onChange={setCommit}
            required
            placeholder="40-hex git sha of the pinned infra checkout"
            error={commit.trim() !== "" && !commitValid ? "must be a 40-hex git sha" : null}
          />
        )}
      </FormShell>
    </div>
  );
}
